import Phaser from 'phaser';
import { Organelle, OrganelleConfig } from './Organelle';
import { SharedResources } from '../utils/SharedResources';

export interface LysosomeConfig extends OrganelleConfig {
  // Lysosome-specific properties
  size: number; // Size of the lysosome
  digestiveEnzymes: number; // Digestive capacity (1-10)
  pulsate?: boolean; // Whether to add pulsation animation
}

export class Lysosome extends Organelle {
  private digestivePower: number = 5;
  private lysoSprite!: Phaser.GameObjects.Sprite;
  private pulseAnimation: Phaser.Tweens.Tween | null = null;
  private floatOffsetX: number = 0;
  private floatOffsetY: number = 0;
  private originalX: number = 0;
  private originalY: number = 0;
  private floatSpeed: number = 0;
  private floatAngle: number = 0;
  private floatRadius: number = 0;
  private lastCheckedPosition: { x: number, y: number } | null = null;
  private updateTimer: number = 0;

  // Default configuration for lysosomes
  private static readonly LYSOSOME_CONFIG: Partial<LysosomeConfig> = {
    digestiveEnzymes: 5,
    size: 15, // Smaller than mitochondria
    color: 0xFFFFFF, // Use white (no tint) to preserve original colors
    alpha: 0.9,
    outline: true,
    outlineColor: 0xFFFFFF, // No outline color change
    movementDelay: 0.8, // Allow some movement (less than mitochondria)
    wobbleAmount: 0.1, // Subtle wobble
    maintainPosition: true,
    detailLevel: 'minimal',
    depth: 85, // Between nucleus (80) and mitochondria (90)
    type: 'lysosome',
    pulsate: true
  };

  constructor(scene: Phaser.Scene, x: number, y: number, config?: Partial<LysosomeConfig>) {
    // First create the base config with type
    const baseConfig: Partial<OrganelleConfig> = {
      type: 'lysosome'
    };
    
    // Then merge with lysosome-specific defaults and provided config
    const fullConfig = {
      ...Lysosome.LYSOSOME_CONFIG,
      ...config,
      ...baseConfig // Ensure type is not overridden
    };
    
    // Log the initial position
    console.log(`Creating lysosome at position (${x}, ${y})`);
    
    super(scene, x, y, fullConfig);
    
    // Verify position after parent constructor
    console.log(`Lysosome position after parent constructor: (${this.x}, ${this.y})`);
    
    // Remember original position for floating movement
    this.originalX = x;
    this.originalY = y;
    
    // Get SharedResources instance
    const sharedResources = SharedResources.getInstance();
    
    // Check if we need to preload the texture
    if (!sharedResources.hasLysosomeTexture() && 
        !scene.textures.exists(SharedResources.LYSOSOME_TEXTURE_KEY)) {
      console.log('Lysosome texture not loaded, preloading now');
      sharedResources.preloadLysosomeTexture(scene);
      
      // Listen for the complete event to create the sprite after loading
      scene.load.once('complete', () => {
        this.createSprite(scene, fullConfig);
      });
      
      // Start the load if it hasn't started yet
      if (scene.load.isLoading() === false) {
        scene.load.start();
      }
    } else {
      // Texture is already available, create sprite immediately
      this.createSprite(scene, fullConfig);
    }
    
    // Initialize floating movement parameters
    this.initFloatingMovement();
  }
  
  /**
   * Creates the sprite for the lysosome
   * @param scene The scene to add the sprite to
   * @param fullConfig The complete configuration
   */
  private createSprite(scene: Phaser.Scene, fullConfig: Partial<LysosomeConfig>): void {
    // Create sprite from the shared resource
    const sharedResources = SharedResources.getInstance();
    
    // Use minimal texture for lysosome
    if (!sharedResources.hasLysosomeTexture() && 
        !scene.textures.exists(SharedResources.LYSOSOME_TEXTURE_KEY)) {
      console.log('Minimal lysosome texture not loaded, preloading now');
      sharedResources.preloadLysosomeTexture(scene);
      
      // Listen for the complete event to create the sprite after loading
      scene.load.once('complete', () => {
        // Create the sprite after loading completes
        this.lysoSprite = sharedResources.createLysosomeSprite(scene, 0, 0);
        this.finishSpriteSetup(fullConfig);
      });
      
      // Start the load if it hasn't started yet
      if (scene.load.isLoading() === false) {
        scene.load.start();
      }
      return; // Exit early, waiting for texture to load
    }
    
    // Use the lysosome texture
    this.lysoSprite = sharedResources.createLysosomeSprite(scene, 0, 0);
    
    // Finish setting up the sprite
    this.finishSpriteSetup(fullConfig);
  }
  
  /**
   * Finishes setting up the sprite after creation
   */
  private finishSpriteSetup(fullConfig: Partial<LysosomeConfig>): void {
    // Set alpha from config
    this.lysoSprite.setAlpha(fullConfig.alpha || 0.9);
    
    // Apply tint if color is specified
    if (fullConfig.color) {
      this.lysoSprite.setTint(fullConfig.color);
    } else {
      this.lysoSprite.clearTint();
    }
    
    // Set blend mode
    this.lysoSprite.setBlendMode(Phaser.BlendModes.NORMAL);
    
    // Add random rotation for more natural appearance
    this.lysoSprite.setRotation(Math.random() * Math.PI * 2);
    
    // Add to the container
    this.add(this.lysoSprite);
    
    // Set size based on config
    this.updateSize(fullConfig);
    
    // Add subtle pulsation for visual interest
    if (fullConfig.pulsate) {
      this.addPulsation();
    }
  }
  
  /**
   * Initialize subtle floating movement parameters
   */
  private initFloatingMovement(): void {
    // Save the original position - use the current position
    // This ensures we maintain the position set during creation
    this.originalX = this.x;
    this.originalY = this.y;
    
    // Update initialPosition if it exists to match current position
    if (this.initialPosition) {
      this.initialPosition.set(this.x, this.y);
    }
    
    // Update targetPosition if it exists to match current position
    if (this.targetPosition) {
      this.targetPosition.set(this.x, this.y);
    }
    
    // Generate random parameters that will be consistent for this lysosome
    const uniqueSeed = Math.random(); // Generate random seed
    
    // Generate movement parameters with reduced values for more stability
    this.floatSpeed = 0.15 + uniqueSeed * 0.2; // Between 0.15 and 0.35 (reduced from 0.3-0.7)
    this.floatAngle = uniqueSeed * Math.PI * 2; // Random starting angle
    this.floatRadius = 0.5 + uniqueSeed * 1.0; // Small radius of 0.5-1.5 pixels (reduced from 1-3)
    
    // Log initial position for debugging
    console.log(`Initialized lysosome floating movement at position (${this.x}, ${this.y})`);
  }
  
  /**
   * Update the size of the lysosome based on the configuration
   */
  private updateSize(config: Partial<LysosomeConfig>): void {
    // Get desired size from config
    const size = config.size || 15;
    
    // The lysosome image is 37x38px, and we want to scale it proportionally
    // to the mitochondria, which are 80px scaled to 0.25 (resulting in 20px)
    
    // To achieve a similar proportion, we'll scale the lysosome down
    // Mitochondria: 80px * 0.25 = 20px
    // Lysosome: 37px * 0.4 ≈ 15px (desired size)
    const scale = 0.4;  // Scale factor to make the lysosome approximately 15px wide
    
    this.lysoSprite.setScale(scale);

    console.log(`Scaled lysosome to approximately 15px width (40% of natural size 37x38px)`);
  }

  /**
   * Override the update method to add subtle autonomous movement
   */
  update(delta: number, cellX: number, cellY: number, cellVelocity?: Phaser.Math.Vector2): void {
    // Call parent update with reduced frequency to prevent jitter
    this.updateTimer = (this.updateTimer || 0) + delta;
    
    // If we've accumulated enough delta time, do the update
    if (this.updateTimer >= 100) { // 100ms threshold
      super.update(delta, cellX, cellY, cellVelocity);
      this.updateTimer = 0; // Reset the timer
      
      // Update position tracking
      const currentX = this.x;
      const currentY = this.y;
      
      if (!this.lastCheckedPosition) {
        this.lastCheckedPosition = { x: currentX, y: currentY };
      }
    }
    
    // Apply subtle autonomous movement
    this.applyFloatingMovement(delta);
  }
  
  /**
   * Apply subtle floating movement with improved boundary handling
   */
  private applyFloatingMovement(delta: number): void {
    // Update angle for circular floating motion very slowly
    this.floatAngle += this.floatSpeed * (delta / 1000);
    
    // Calculate new float offsets - create very subtle movement
    this.floatOffsetX = Math.cos(this.floatAngle) * this.floatRadius;
    this.floatOffsetY = Math.sin(this.floatAngle) * this.floatRadius;
    
    // Only apply if we're still using the original target position
    if (this.targetPosition && this.initialPosition) {
      // Get distance from center
      const currentDistance = Math.sqrt(this.x * this.x + this.y * this.y);
      
      // If we're too close to nucleus or too close to cell membrane, don't apply floating movement
      const minSafeDistance = 47; // Adjusted for smaller nucleus (was 50 previously)
      const maxSafeDistance = 85; // Unchanged - Cell membrane - buffer
      
      if (currentDistance < minSafeDistance || currentDistance > maxSafeDistance) {
        // Calculate angle from center
        const angleToCenter = Math.atan2(this.y, this.x);
        
        // Calculate ideal distance - middle of intermediate zone (52-75)
        const idealDistance = 62; // Adjusted to match updated zone (was 65 previously)
        
        // Stronger correction if we're very close to boundaries
        const correctionFactor = 0.1;
        
        // Calculate target position at ideal distance
        const targetX = Math.cos(angleToCenter) * idealDistance;
        const targetY = Math.sin(angleToCenter) * idealDistance;
        
        // Move toward ideal position with smooth transition
        this.x += (targetX - this.x) * correctionFactor;
        this.y += (targetY - this.y) * correctionFactor;
        
        // Update tracking positions
        this.initialPosition.set(this.x, this.y);
        this.targetPosition.set(this.x, this.y);
        
        // Skip floating movement this frame
        return;
      }
      
      // For normal movement when safely away from boundaries, apply very subtle movement
      const newX = this.initialPosition.x + this.floatOffsetX;
      const newY = this.initialPosition.y + this.floatOffsetY;
      
      // Apply extremely gentle movement with minimal effect
      this.x += (newX - this.x) * 0.05; // Reduced from 0.1 for more stability
      this.y += (newY - this.y) * 0.05;
      
      // Update target position for consistency
      this.targetPosition.set(this.x, this.y);
    }
  }

  // Override the parent draw method to prevent drawing the background circle
  protected draw(): void {
    // Clear any previous graphics
    this.mainShape.clear();
    
    // We're not drawing a fallback shape here because we're using the sprite instead
  }

  destroy(fromScene?: boolean): void {
    // Stop and destroy any animations
    if (this.pulseAnimation) {
      this.pulseAnimation.stop();
      this.pulseAnimation.remove();
      this.pulseAnimation = null;
    }
    
    // Call parent destroy method
    super.destroy(fromScene);
  }

  getDigestivePower(): number {
    return this.digestivePower;
  }

  setDigestivePower(amount: number): void {
    this.digestivePower = Math.max(0, amount);
  }

  /**
   * Add subtle pulsation to the lysosome
   */
  private addPulsation(): void {
    if (!this.scene) return;
    
    // Generate a consistent duration using the position as input
    const positionHash = Math.abs(this.x * 100 + this.y * 100);
    const pulseDuration = 1500 + (positionHash % 1000); // 1.5-2.5 seconds
    
    // Using the scale value we set in updateSize as the base scale
    const baseScale = 0.4; // Match the scale we set in updateSize
    const minScale = baseScale * 0.95; // 5% smaller at minimum
    const maxScale = baseScale * 1.05; // 5% larger at maximum
    
    // Log that we're adding pulsation
    console.log(`Adding pulsation to lysosome at (${this.x.toFixed(2)}, ${this.y.toFixed(2)}) with duration ${pulseDuration}ms`);
    
    // Stop any existing animation
    if (this.pulseAnimation) {
      this.pulseAnimation.stop();
      this.pulseAnimation.remove();
    }
    
    this.pulseAnimation = this.scene.tweens.add({
      targets: this.lysoSprite,
      scaleX: { from: minScale, to: maxScale },
      scaleY: { from: minScale, to: maxScale },
      duration: pulseDuration,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: (positionHash % 500) // Stagger start time (0-500ms)
    });
  }
} 